import cn from 'clsx'
import s from './Layout.module.css'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { CommerceProvider } from '@framework'
import { useUI } from '@components/ui/context'
import { useAcceptCookies } from '@lib/hooks/useAcceptCookies'
import LoadingDots from '@components/ui/LoadingDots/LoadingDots'
import { CheckoutProvider } from '@components/checkout/context'
import type { Page } from '@commerce/types/page'
import type { Category } from '@commerce/types/site'
import Intercom from '../Intercom/Intercom'
import Head from 'next/head'
import { AlertProvider } from '../../../context/alertContext'
import CookieProvider from 'context/cookieContext'
import { useContext, useEffect, useMemo, useRef, useState, Dispatch, SetStateAction } from 'react'
import Cookies from 'js-cookie'
import { ShapeFilterProvider } from 'context/shapeFilterContext'
import axios from 'axios'
import { LiveChatLoaderProvider } from 'react-live-chat-loader'
import { useBreakpoints } from 'hooks/useTailwindBreakpoints'
import { CookieContext } from 'context/cookieContext'
import type { Link as LinkProps } from '../UserNav/MenuSidebarView'
import Navbar from '../Navbar'

const Loading = () => (
  <div className="w-80 h-80 flex items-center text-center justify-center p-3">
    <LoadingDots />
  </div>
)
const dynamicProps = {
  loading: Loading,
}

const Alert = dynamic(() => import('@components/common/Alert'), {
  ...dynamicProps,
})

const ModalGate = () => {
  const { displayModal } = useUI()
  if (!displayModal) return null
  
  const LazyModal = dynamic(() => import('@components/layout/ModalUI'), {
    ...dynamicProps,
    ssr: false,
  })

  return <LazyModal />
}

const SidebarGate = ({ links }: { links: LinkProps[] }) => {
  const { displaySidebar } = useUI()
  if (!displaySidebar) return null
  
  const LazySidebar = dynamic(() => import('@components/layout/SidebarUI'), {
    ...dynamicProps,
    ssr: false,
  })
  return <LazySidebar links={links} />
}

const FeatureBarGate = ({title} : {title: string}) => {
  const { acceptedCookies } = useContext(CookieContext)
  const idle = useIdle(1200)

  if (acceptedCookies || !idle) return null

  const FeatureBar = dynamic(() => import('@components/common/FeatureBar'), {
    ...dynamicProps,
    ssr: false,
  })
  
  return (
    <FeatureBar title={title} />
  )
}

const FooterGate = ({
  pages,
  className,
  delay = 1200,
}: {
  pages?: Page[]
  className?: string
  delay?: number
}) => {
  const idle = useIdle(delay)
  

  const FooterDynRef = useRef<null | React.ComponentType<any>>(null)
  if (idle && !FooterDynRef.current) {
    FooterDynRef.current = dynamic(() => import('@components/common/Footer'), {
      ssr: false,
      ...dynamicProps,
    })
  }

  if (!FooterDynRef.current) return null

  const FooterDyn = FooterDynRef.current
  return <FooterDyn pages={pages} className={className} />
}

const Modal100OffGate = ({setShowPopUp}:{setShowPopUp: Dispatch<SetStateAction<boolean>>}) => {
  const idle = useIdle(1200)

  if(!idle) return null

  const Modal100Off = dynamic(() => import('@components/yadav/ui/Modal100Off'), {
    ...dynamicProps,
    ssr: false,
  })
  
  return (
    <Modal100Off setShowPopUp={setShowPopUp} />
  )
}

interface Props {
  pageProps: {
    pages?: Page[]
    categories: Category[]
  }
}

const Layout: React.FC<Props> = ({
  children,
  pageProps: { categories = [], ...pageProps },
}) => {
  const { locale = 'en-US' } = useRouter()
  const navBarlinks = categories.slice(0, 2).map((c) => ({
    label: c.name,
    href: `/search/${c.slug}`,
  }))
  const [prevScrollPos, setPrevScrollPos] = useState(0)
  const [isNavbarFixed, setIsNavbarFixed] = useState(false)
  const [showPopUp, setShowPopUp] = useState(false)

  const organizationJsonLd = useMemo(() => {
    const organizationData = {
      '@context': 'https://schema.org/',
      '@type': 'Organization',
      '@id' : `${process.env.baseUrl}/#organization`,
      name: 'Yadav Diamonds and Jewelry',
      url: `${process.env.baseUrl}`,
      logo: `${process.env.baseUrl}/next-static/images/layout/logo_desktop.svg`,
      sameAs: [
        'https://www.facebook.com/Yadavjewelry',
        'https://www.instagram.com/yadavjewelry',
        'https://www.pinterest.com/yadavdiamondsandjewelry',
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '888-968-8810',
        contactType: 'customer service',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '170 Grant Ave, Suite 500',
        addressLocality: 'San Francisco',
        addressRegion: 'CA',
        postalCode: '94108',
        addressCountry: 'US',
      },
    }

    return {
      __html: JSON.stringify(organizationData),
    }
  },[])

  const localBusinessJsonLd = useMemo(() => {
    const localBusinessData = {
      '@context': 'https://schema.org/',
      '@type': 'LocalBusiness',
      '@id' : `${process.env.baseUrl}/#localbusiness`,
      "parentOrganization": { '@id': `${process.env.baseUrl}/#organization` },
      "name": "Yadav Diamonds & Jewelry - San Francisco",
      address: {
        '@type': 'PostalAddress',
        streetAddress: '170 Grant Ave, Suite 500',
        addressLocality: 'San Francisco',
        addressRegion: 'CA',
        postalCode: '94108',
        addressCountry: 'US',
      },
      telephone: '888-968-8810',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '10:00',
          closes: '18:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Saturday',
          opens: '11:00',
          closes: '17:00',
        },
      ],
      url: `${process.env.baseUrl}`,
      image: `${process.env.baseUrl}/next-static/images/layout/logo_desktop.svg`,
      priceRange: '$$$',
      paymentAccepted: 'Cash, Credit Card',
      sameAs: [
        'https://www.facebook.com/Yadavjewelry',
        'https://www.instagram.com/yadavjewelry',
        'https://www.pinterest.com/yadavdiamondsandjewelry',
      ],
    }

    return {
      __html: JSON.stringify(localBusinessData),
    }
  },[])

  const [navHeight, setNavHeight] = useState<number>(220)
  const {md: md} = useBreakpoints()

  // Intercom key
  const APP_ID = 'orpdg3jb'

  // The navHeight value is used in the handleScroll event
  useEffect(() => {
    if (md) {
      setNavHeight(220)
    }else {
      setNavHeight(132)
    }
  }, [md])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY
      if (currentScrollPos > prevScrollPos) {
        // Scroll down
        if (currentScrollPos > navHeight) {
          setIsNavbarFixed(true)
        }
      } else {
        // Scroll up
        if (currentScrollPos === 0) {
          setIsNavbarFixed(false)
        }
      }
      setPrevScrollPos(currentScrollPos)
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [navHeight, prevScrollPos])

  useEffect(() => {
    const popUpWasShowed = Cookies.get('popup_100off')

    if (!popUpWasShowed) {
      const apiUrl2 = `${process.env.NEXT_PUBLIC_SPREE_API_HOST}/yadav_api/v1`;

      axios.get(`${apiUrl2}/popup_setting`)
        .then(response => {
          if (response.data.is_active) {
            const interval = setTimeout(() => {
              setShowPopUp(true)
              clearInterval(interval)
            }, 7000)
          }
        })
        .catch(error => {
          console.error('Error fetching pop-up setting:', error)
        })
    } else {
      setShowPopUp(false)
    }
  }, [])
  
  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={organizationJsonLd}
          key="organization-jsonld"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={localBusinessJsonLd}
          key="local-business-jsonld"
        />
      </Head>
      <CommerceProvider locale={locale}>
        <AlertProvider>
          <CookieProvider>
            <LiveChatLoaderProvider providerKey={APP_ID} provider='intercom'>
              <ShapeFilterProvider>
                <div className={cn(s.root)}>
                  <Navbar links={navBarlinks} />
                  <main className={cn('fit', { [s.stickHeader]: isNavbarFixed })}>
                    <Alert />
                    {showPopUp && <Modal100OffGate setShowPopUp={setShowPopUp} />}
                    {children}
                  </main>
                  <FooterGate pages={pageProps.pages} className="bg-primary" />
                  <ModalGate />
                  <CheckoutProvider>
                    <SidebarGate links={navBarlinks} />
                  </CheckoutProvider>
                  <FeatureBarGate title="This website uses cookies to ensure your best shopping experience and track your cart." />
                </div>
                <Intercom />
              </ShapeFilterProvider>
            </LiveChatLoaderProvider>
          </CookieProvider>
        </AlertProvider>
      </CommerceProvider>
    </>
  )
}

export default Layout

// Helper to defers rendering non-critical UI until the browser is idle
function useIdle(delay = 1200) {
  const [idle, setIdle] = useState(false)
  useEffect(() => {
    const onIdle = () => setIdle(true)
    if ('requestIdleCallback' in window) {
      ;(window as any).requestIdleCallback(onIdle, { timeout: delay })
    } else {
      const t = setTimeout(onIdle, delay)
      return () => clearTimeout(t)
    }
  }, [delay])
  return idle
}